import { useEffect, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import WarehouseIcon from '@mui/icons-material/Warehouse';
import axios from '../../utils/axios';
import { useNotify } from '../NotifyProvider';
import {
  fulfillmentApiBase,
  withAccountBody,
  withAccountParams,
} from '../../utils/fulfillmentApi.mjs';

const EMPTY = {
  source_store_id: null,
  source_store_name: null,
  target_store_id: null,
  target_store_name: null,
  move_on_close: false,
};

/**
 * Диалог складов МойСклад для внутреннего подбора FBS (WB / Ozon / Yandex).
 * API: GET/PUT /fulfillment/unified/picking-settings/:accountId
 * Справочник: GET /mswb/ms-stores
 */
export default function PickingMsSettingsDialog({
  open,
  accountId,
  accountName = '',
  marketplace = 'wb',
  onClose,
  onSaved,
}) {
  const notify = useNotify();
  const [form, setForm] = useState({ ...EMPTY });
  const [msStores, setMsStores] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!open || !accountId) return undefined;
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError('');
      setForm({ ...EMPTY });
      setMsStores([]);
      try {
        const [settingsResponse, storesResponse] = await Promise.all([
          axios.get(`${fulfillmentApiBase(marketplace)}/picking-settings/${accountId}`, {
            params: withAccountParams(marketplace, accountId),
          }),
          axios.get('/mswb/ms-stores', { silent: true }).catch(() => ({ data: [] })),
        ]);
        if (cancelled) return;
        const settings = settingsResponse.data || {};
        setForm({
          source_store_id: settings.source_store_id || null,
          source_store_name: settings.source_store_name || null,
          target_store_id: settings.target_store_id || null,
          target_store_name: settings.target_store_name || null,
          move_on_close: Boolean(settings.move_on_close),
        });
        const stores = Array.isArray(storesResponse.data)
          ? storesResponse.data
          : (storesResponse.data?.items || storesResponse.data?.stores || []);
        setMsStores(stores.map((store) => ({
          id: String(store.id || store.store_id || ''),
          name: store.name || store.store_name || String(store.id || ''),
        })).filter((store) => store.id));
      } catch (err) {
        if (!cancelled) {
          setError(err?.response?.data?.message || err.message || 'Не удалось загрузить настройки подбора');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [open, accountId, marketplace]);

  const storeOption = (storeId, storeName) => {
    if (!storeId) return null;
    return msStores.find((store) => store.id === String(storeId))
      || { id: String(storeId), name: storeName || String(storeId) };
  };

  const handleClose = () => {
    if (saving) return;
    onClose?.();
  };

  const save = async () => {
    if (!accountId) return;
    if (form.move_on_close && (!form.source_store_id || !form.target_store_id)) {
      setError('Для перемещения при закрытии задания нужно выбрать оба склада МойСклад');
      return;
    }
    if (form.source_store_id && form.target_store_id
      && form.source_store_id === form.target_store_id) {
      setError('Склад-источник и склад сборки должны различаться');
      return;
    }
    setSaving(true);
    setError('');
    try {
      await axios.put(
        `${fulfillmentApiBase(marketplace)}/picking-settings/${accountId}`,
        withAccountBody(marketplace, accountId, form),
      );
      notify.success('Настройки складов подбора сохранены');
      onSaved?.(form);
      onClose?.();
    } catch (err) {
      setError(err?.response?.data?.message || err.message || 'Ошибка сохранения');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <WarehouseIcon color="primary" />
          Склады подбора
          {accountName ? `: ${accountName}` : ''}
        </Box>
      </DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2, mt: 1 }}>
          Эти склады используются для внутреннего подбора FBS (ТСД): товар собирают
          со склада-источника и при закрытии задания перемещают на склад сборки
          (зону отгрузки). Это не склады приёмки возвратов и не связки МС↔МП.
        </Typography>

        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress size={28} />
          </Box>
        )}

        {!loading && (
          <Stack spacing={2}>
            {!msStores.length && (
              <Alert severity="info">
                Список складов МойСклад сейчас недоступен (нет креденшелов или права). Можно
                сохранить UUID вручную через уже выбранные значения, либо сначала настроить доступ к МС.
              </Alert>
            )}
            <Autocomplete
              options={msStores}
              value={storeOption(form.source_store_id, form.source_store_name)}
              getOptionLabel={(option) => option?.name || ''}
              isOptionEqualToValue={(option, value) => String(option?.id) === String(value?.id)}
              onChange={(_event, value) => setForm((prev) => ({
                ...prev,
                source_store_id: value?.id || null,
                source_store_name: value?.name || null,
              }))}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Склад-источник МойСклад"
                  helperText="Обязателен: откуда резервируем остаток при подборе"
                />
              )}
            />
            <Autocomplete
              options={msStores}
              value={storeOption(form.target_store_id, form.target_store_name)}
              getOptionLabel={(option) => option?.name || ''}
              isOptionEqualToValue={(option, value) => String(option?.id) === String(value?.id)}
              onChange={(_event, value) => setForm((prev) => ({
                ...prev,
                target_store_id: value?.id || null,
                target_store_name: value?.name || null,
              }))}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Склад сборки / назначения"
                  helperText="Куда перемещаем подобранный товар (если включено перемещение)"
                />
              )}
            />
            <FormControlLabel
              control={(
                <Switch
                  size="small"
                  checked={Boolean(form.move_on_close)}
                  onChange={(event) => setForm((prev) => ({
                    ...prev,
                    move_on_close: event.target.checked,
                  }))}
                />
              )}
              label="Создавать перемещение при закрытии задания"
            />
          </Stack>
        )}

        {error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={saving}>Отмена</Button>
        <Button
          variant="contained"
          onClick={save}
          disabled={saving || loading || !accountId}
        >
          {saving ? 'Сохранение…' : 'Сохранить'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
